import { useMemo } from 'react';

import { useConversationContext } from '@/contexts/v2/ConversationContext';
import { cn } from '@/lib/utils';

import { ChatTypingDots } from './ChatTypingDots';
import { derivePeekStatus } from './peekStatus';

export interface ConversationStatusLineProps {
  className?: string;
}

/**
 * Compact, always-visible status cue for detailed mode — surfaces the same
 * "Ready when you are → Thinking… → Responding → Anaka replied" progression the
 * peek pill shows (derivePeekStatus, FR-007). Detailed mode renders only the
 * LastResponseCard (which ignores in-flight state and is `null` until the first
 * assistant reply), so a sent message produced no visible cue that Anaka was
 * working — this line fills that gap. Focused mode already shows the equivalent
 * via the transcript's typing bubble, so this is for detailed mode only.
 *
 * Reads runtime state from the conversation context and renders an
 * `aria-live` region so transitions are announced to assistive tech.
 */
export function ConversationStatusLine({ className }: ConversationStatusLineProps) {
  const { turns, isThinking, isResponding, voiceState } = useConversationContext();

  const hasAnakaReply = useMemo(
    () => turns.some((t) => t.role === 'assistant'),
    [turns],
  );

  const status = useMemo(
    () => derivePeekStatus({ isThinking, isResponding, voiceState, hasAnakaReply }),
    [isThinking, isResponding, voiceState, hasAnakaReply],
  );

  const busy = status.variant === 'thinking' || status.variant === 'responding';

  return (
    <div
      data-testid="conversation-status-line"
      data-variant={status.variant}
      role="status"
      aria-live="polite"
      className={cn('flex items-center gap-2 px-1', className)}
    >
      {busy ? (
        // Decorative dots — wrapped aria-hidden so the visible label (announced
        // via the aria-live region) isn't doubled by ChatTypingDots' sr-only.
        <span aria-hidden className="inline-flex">
          <ChatTypingDots />
        </span>
      ) : (
        <span
          aria-hidden
          className="size-1.5 shrink-0 rounded-full"
          style={{ background: status.accent }}
        />
      )}
      <span
        className="font-semibold"
        style={{
          color: status.accent,
          fontSize: 11.5,
          fontFamily: "'Open Sans', sans-serif",
        }}
      >
        {status.label}
      </span>
    </div>
  );
}
